import React, { useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'

import { httpOk, GET } from 'helpers/api'
import { formatMoney } from 'helpers/helpers'
import { FilterSegment } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsOptions from 'pages/main/analytics/parts/useAnalyticsOptions'
import { buildAnalyticsUrl } from 'pages/main/analytics/parts/analyticsDates'
import ChainFilterBar, { useChainFilters, FilterField, FilterSelect, ExcelButton } from '../parts/ChainFilterBar'
import { downloadExcel } from '../parts/chainExcel'
import {
	money, percent, columnStats, heatStyle, intensityStyle, chainColor,
	FillCell, useSort, SortTh, ChainKpi, HeatLegend, ChainEmpty, TableSearch,
} from '../parts/chainUi'
import { qty, qtyUnit, productTypeOptions, productTypeLabel, ProductNameCell, FOOT_EXTRA_STYLE } from './chainStockUi'

// Что показывает ячейка матрицы: количество или стоимость остатка
const CELL_MODES = ['quantity', 'value']

// Отрицательный остаток — красная заливка независимо от шкалы строки
const NEGATIVE_STYLE = intensityStyle(0.4, 1, 'danger')

function Balance() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [cellMode, setCellMode] = useState('quantity')
	const [search, setSearch] = useState('')
	const [details, setDetails] = useState(null)

	const filters = useChainFilters({ wms_ids: [], category_ids: [], product_type_ids: [], nonzero: 1, negative: 0 }, searchWithFilter)
	const { draft, patch } = filters
	const options = useAnalyticsOptions(['warehouses', 'categories'])

	async function searchWithFilter(filter) {
		// Остаток — на текущий момент: период в запрос не уходит
		const { start_date, end_date, ...rest } = filter // eslint-disable-line no-unused-vars
		const response = await GET(buildAnalyticsUrl('/chain/report/stock-balance', rest), {}, { loader: false })
		if (httpOk(response)) setData(response.data)
	}

	const branches = useMemo(() => data?.branches || [], [data])
	const totals = data?.totals || {}
	const warehouseById = useMemo(() => Object.fromEntries((data?.warehouses || []).map(item => [item.id, item])), [data])

	// Склады в фильтре — только выбранных филиалов, с названием филиала
	const warehouseOptions = useMemo(() => {
		const posNames = Object.fromEntries(options.poses.map(pos => [pos.id, pos.short_name || pos.name]))
		const selected = draft.pos_ids || []
		return options.warehouses
			.filter(wms => selected.length === 0 || selected.includes(wms.pos_id))
			.map(wms => ({ id: wms.id, name: posNames[wms.pos_id] ? `${wms.name} · ${posNames[wms.pos_id]}` : wms.name }))
	}, [options.warehouses, options.poses, draft.pos_ids])

	function cellValue(row, posId) {
		const cell = row.cells?.[posId]
		if (!cell) return null
		return cellMode === 'quantity' ? cell.balance : cell.value
	}

	function totalValue(row) {
		return cellMode === 'quantity' ? row.total_balance : row.total_value
	}

	function formatCell(value) {
		if (value === null || value === undefined) return '—'
		if (cellMode === 'quantity') return Math.abs(value) < 0.0001 ? '0' : qty(value)
		return Math.abs(value) < 0.5 ? '0' : money(value)
	}

	const rows = useMemo(() => {
		const list = data?.rows || []
		const query = search.trim().toLowerCase()
		if (!query) return list
		return list.filter(row => row.product_name.toLowerCase().includes(query) || (row.category_name || '').toLowerCase().includes(query))
	}, [data, search])

	const readSortValue = (row, key) => {
		if (key.startsWith('pos:')) return cellValue(row, Number(key.slice(4)))
		if (key === 'total') return totalValue(row)
		return row[key]
	}
	const { sorted, sort, toggle } = useSort(rows, 'total_value', 'desc', readSortValue)

	const branchStats = useMemo(() => ({
		negative_positions: columnStats(branches, 'negative_positions'),
	}), [branches])
	const maxBranchValue = useMemo(() => Math.max(0, ...branches.map(item => item.value)), [branches])
	const richest = branches.length > 0 ? [...branches].sort((a, b) => b.value - a.value)[0] : null

	function openDetails(row, posId = null) {
		setDetails({ row, posId })
	}

	// Строки расшифровки: склады каждого филиала по товару
	function warehouseRows(row) {
		const result = []
		branches.forEach(branch => {
			const cell = row.cells?.[branch.pos_id]
			if (!cell) return
			(cell.wms || []).forEach(item => {
				result.push({
					pos_id: branch.pos_id,
					pos_name: branch.pos_name,
					wms_name: warehouseById[item.wms_id]?.name || (item.wms_id ? `#${item.wms_id}` : '—'),
					balance: item.balance,
					price: item.price,
					value: item.balance * item.price,
				})
			})
		})
		return result
	}

	function exportExcel() {
		if (!data) return
		const subtitle = t('chain_stock_now')
		const all = data.rows || []
		const productColumns = [
			{ label: t('name2'), type: 'text', width: 34 },
			{ label: t('unit'), type: 'text', width: 8 },
			{ label: t('category'), type: 'text', width: 20 },
			{ label: t('type'), type: 'text', width: 12 },
		]
		const productCells = row => [row.product_name, row.unit_name || '', row.category_name || '', productTypeLabel(t, row.product_type_id)]
		const detailRows = []
		all.forEach(row => {
			warehouseRows(row).forEach(item => {
				detailRows.push([...productCells(row), item.pos_name, item.wms_name, item.balance, item.price, Math.round(item.value)])
			})
		})

		downloadExcel(`${t('chain_stock_title')} ${new Date().toLocaleDateString('ru-RU')}`, [
			{
				name: t('quantity'),
				title: `${t('chain_stock_title')} — ${t('quantity')}`,
				subtitle,
				columns: [
					...productColumns,
					...branches.map(branch => ({ label: branch.pos_name, type: 'number' })),
					{ label: t('total'), type: 'number' },
					{ label: t('chain_avg_cost'), type: 'money' },
					{ label: t('chain_stock_value'), type: 'money' },
				],
				rows: all.map(row => [
					...productCells(row),
					...branches.map(branch => row.cells?.[branch.pos_id]?.balance ?? ''),
					row.total_balance, row.avg_price, row.total_value,
				]),
				footer: [t('total'), '', '', '', ...branches.map(() => ''), '', '', totals.value],
			},
			{
				name: t('chain_stock_value'),
				title: `${t('chain_stock_title')} — ${t('chain_stock_value')}`,
				subtitle,
				columns: [
					...productColumns,
					...branches.map(branch => ({ label: branch.pos_name, type: 'money' })),
					{ label: t('total'), type: 'money' },
				],
				rows: all.map(row => [
					...productCells(row),
					...branches.map(branch => row.cells?.[branch.pos_id]?.value ?? ''),
					row.total_value,
				]),
				footer: [t('total'), '', '', '', ...branches.map(branch => branch.value), totals.value],
			},
			{
				name: t('warehouses'),
				title: `${t('chain_stock_title')} — ${t('warehouses')}`,
				subtitle,
				columns: [
					...productColumns,
					{ label: t('chain_branch'), type: 'text', width: 26 },
					{ label: t('wms'), type: 'text', width: 22 },
					{ label: t('balance2'), type: 'number' },
					{ label: t('chain_avg_cost'), type: 'money' },
					{ label: t('chain_stock_value'), type: 'money' },
				],
				rows: detailRows,
			},
			{
				name: t('chain_branches'),
				title: `${t('chain_stock_title')} — ${t('chain_branches')}`,
				subtitle,
				columns: [
					{ label: t('chain_branch'), type: 'text', width: 30 },
					{ label: t('chain_stock_value'), type: 'money' },
					{ label: t('share'), type: 'percent', width: 9 },
					{ label: t('chain_stock_in_stock'), type: 'int', width: 12 },
					{ label: t('chain_stock_negative'), type: 'int', width: 12 },
					{ label: t('chain_stock_negative_value'), type: 'money' },
					{ label: t('chain_stock_no_cost'), type: 'int', width: 14 },
				],
				rows: [...branches].sort((a, b) => b.value - a.value).map(branch => [
					branch.pos_name, branch.value, branch.share, branch.positions, branch.negative_positions, branch.negative_value, branch.no_cost_positions,
				]),
				footer: [t('total'), totals.value, 100, totals.positions, totals.negative_cells, totals.negative_value, totals.no_cost_cells],
			},
		])
	}

	function exportDetails() {
		if (!details) return
		const row = details.row
		downloadExcel(`${t('chain_stock_title')} — ${row.product_name}`, [{
			name: t('warehouses'),
			title: `${row.product_name}${row.unit_name ? `, ${row.unit_name}` : ''}`,
			subtitle: t('chain_stock_now'),
			columns: [
				{ label: t('chain_branch'), type: 'text', width: 28 },
				{ label: t('wms'), type: 'text', width: 24 },
				{ label: t('balance2'), type: 'number' },
				{ label: t('chain_avg_cost'), type: 'money' },
				{ label: t('chain_stock_value'), type: 'money' },
			],
			rows: warehouseRows(row).map(item => [item.pos_name, item.wms_name, item.balance, item.price, Math.round(item.value)]),
			footer: [t('total'), '', row.total_balance, row.avg_price, row.total_value],
		}])
	}

	const modeLabels = {
		quantity: t('quantity'),
		value: t('chain_stock_value'),
	}

	const detailRows = details ? warehouseRows(details.row) : []

	return (
		<>
			<ChainFilterBar
				title={t('chain_stock_title')}
				icon="uil-box"
				subtitle={t('chain_stock_now')}
				filters={filters}
				presets={false}
				wide
				onExcel={exportExcel}
				excelDisabled={!data}
			>
				<FilterField label={t('warehouses')}>
					<FilterSelect isMulti options={warehouseOptions} allLabel={t('chain_all_warehouses')} value={draft.wms_ids} onChange={(ids) => patch({ wms_ids: ids })} />
				</FilterField>
				<FilterField label={t('categories')}>
					<FilterSelect isMulti options={options.categories} allLabel={t('chain_all_categories')} value={draft.category_ids} onChange={(ids) => patch({ category_ids: ids })} />
				</FilterField>
				<FilterField label={t('chain_product_type')}>
					<FilterSelect isMulti options={productTypeOptions(t)} allLabel={t('chain_all_types')} value={draft.product_type_ids} onChange={(ids) => patch({ product_type_ids: ids })} />
				</FilterField>
				<FilterField label={t('chain_stock_show')}>
					<div className="d-flex flex-wrap align-items-center" style={{ gap: '12px', minHeight: '38px' }}>
						<label className="chain-check">
							<input type="checkbox" checked={Number(draft.nonzero) === 1} onChange={(e) => patch({ nonzero: e.target.checked ? 1 : 0 })} />
							{t('chain_stock_only_nonzero')}
						</label>
						<label className="chain-check">
							<input type="checkbox" checked={Number(draft.negative) === 1} onChange={(e) => patch({ negative: e.target.checked ? 1 : 0 })} />
							{t('chain_stock_only_negative')}
						</label>
					</div>
				</FilterField>
			</ChainFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_stock_network_value')} icon="uil-box" color="#5b73e8"
						value={money(totals.value)}
						sub={<>{formatMoney(totals.positions)} {t('chain_stock_positions_lower')}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_stock_negative_title')} icon="uil-exclamation-triangle" color="#f06548"
						value={formatMoney(totals.negative_rows)}
						sub={<>{money(totals.negative_value)} · {formatMoney(totals.negative_cells)} {t('chain_stock_cells_lower')}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_stock_richest')} icon="uil-store" color="#12b886"
						value={richest && richest.value > 0 ? richest.pos_name : '—'}
						sub={richest && richest.value > 0 ? <>{money(richest.value)} · {percent(richest.share)}</> : null} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_stock_no_cost')} icon="uil-question-circle" color="#6c757d"
						value={formatMoney(totals.no_cost_cells)}
						sub={t('chain_stock_no_cost_hint')} />
				</div>
			</div>

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_stock_by_branches')}</h5>
							<FilterSegment
								options={CELL_MODES.map(id => ({ id, label: modeLabels[id] }))}
								value={cellMode}
								onChange={setCellMode}
							/>
						</div>
						<div className="chain-toolbar-group">
							<HeatLegend mode="success" />
							<TableSearch value={search} onChange={setSearch} placeholder={t('chain_search_product')} />
						</div>
					</div>

					<div className="chain-scroll">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky">#</th>
									<SortTh sortKey="product_name" sort={sort} onSort={toggle} className="chain-sticky-2">{t('name2')}</SortTh>
									{branches.map((branch, index) => (
										<SortTh key={branch.pos_id} sortKey={`pos:${branch.pos_id}`} sort={sort} onSort={toggle} className="text-end">
											<span className="chain-dot" style={{ background: chainColor(index) }}></span>
											{branch.pos_name}
										</SortTh>
									))}
									<SortTh sortKey="total" sort={sort} onSort={toggle} className="text-end chain-total-col">{t('total')}</SortTh>
									<SortTh sortKey="avg_price" sort={sort} onSort={toggle} className="text-end" title={t('chain_avg_cost_hint')}>{t('chain_avg_cost')}</SortTh>
									{cellMode === 'quantity' && (
										<SortTh sortKey="total_value" sort={sort} onSort={toggle} className="text-end">{t('chain_stock_value')}</SortTh>
									)}
								</tr>
							</thead>
							<tbody>
								{sorted.length === 0 && <ChainEmpty colSpan={branches.length + (cellMode === 'quantity' ? 5 : 4)} />}
								{sorted.map((row, rowIndex) => {
									const values = branches.map(branch => cellValue(row, branch.pos_id))
									const rowMax = Math.max(0, ...values.map(value => Number(value || 0)))
									return (
										<tr key={row.product_id}>
											<td className="chain-index chain-sticky">{rowIndex + 1}</td>
											<ProductNameCell row={row} t={t} onClick={() => openDetails(row)} title={t('chain_stock_open_wms')} />
											{branches.map((branch, index) => {
												const value = values[index]
												const cell = row.cells?.[branch.pos_id]
												const negative = value !== null && value < -0.0001
												const isMax = value > 0 && value === rowMax && branches.length > 1
												return (
													<td key={branch.pos_id}
														className={`chain-num ${cell ? 'chain-click' : ''}`}
														style={negative ? NEGATIVE_STYLE : intensityStyle(value, rowMax, 'success')}
														title={cell ? `${branch.pos_name}: ${qtyUnit(cell.balance, row.unit_name)} · ${t('chain_avg_cost')} ${cell.price !== null ? money(cell.price) : '—'} · ${money(cell.value)}` : undefined}
														onClick={() => cell && openDetails(row, branch.pos_id)}>
														<span style={{ fontWeight: isMax ? 800 : 400 }}>{formatCell(value)}</span>
														{cell?.no_cost && <i className="uil uil-question-circle ms-1 chain-muted" title={t('chain_stock_no_cost_hint')}></i>}
													</td>
												)
											})}
											<td className="chain-num chain-total-col" style={totalValue(row) < -0.0001 ? NEGATIVE_STYLE : undefined}>
												{formatCell(totalValue(row))}
											</td>
											<td className="chain-num">{row.avg_price !== null ? money(row.avg_price) : '—'}</td>
											{cellMode === 'quantity' && (
												<td className="chain-num" style={row.total_value < -0.5 ? NEGATIVE_STYLE : undefined}>{money(row.total_value)}</td>
											)}
										</tr>
									)
								})}
							</tbody>
							{sorted.length > 0 && (
								<tbody style={FOOT_EXTRA_STYLE}>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('chain_stock_in_stock')}</td>
										{branches.map(branch => (
											<td key={branch.pos_id} className="chain-num">{formatMoney(branch.positions)}</td>
										))}
										<td className="chain-num chain-total-col">{formatMoney(totals.positions)}</td>
										<td></td>
										{cellMode === 'quantity' && <td></td>}
									</tr>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2" title={t('chain_stock_negative_hint')}>{t('chain_stock_negative')}</td>
										{branches.map(branch => (
											<td key={branch.pos_id} className="chain-num" style={heatStyle(branch.negative_positions, branchStats.negative_positions, false)}
												title={money(branch.negative_value)}>
												{formatMoney(branch.negative_positions)}
											</td>
										))}
										<td className="chain-num chain-total-col">{formatMoney(totals.negative_cells)}</td>
										<td></td>
										{cellMode === 'quantity' && <td></td>}
									</tr>
								</tbody>
							)}
							{sorted.length > 0 && (
								<tfoot>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('chain_stock_value')}</td>
										{branches.map(branch => (
											<FillCell key={branch.pos_id} percent={maxBranchValue > 0 ? Math.max(0, branch.value) / maxBranchValue * 100 : 0} tone="primary"
												style={branch.value < 0 ? NEGATIVE_STYLE : undefined}>
												{money(branch.value)}
												<div className="chain-muted">{percent(branch.share)}</div>
											</FillCell>
										))}
										<td className="chain-num chain-total-col">{money(totals.value)}</td>
										<td></td>
										{cellMode === 'quantity' && <td></td>}
									</tr>
								</tfoot>
							)}
						</table>
					</div>
					<div className="chain-muted mt-2">
						<i className="uil uil-info-circle me-1"></i>
						{t('chain_stock_matrix_hint')}
					</div>
				</div>
			</div>

			<ValueChart branches={branches} />

			{/* MODAL DETAILS */}
			<Modal show={!!details} onHide={() => setDetails(null)} size="xl" centered scrollable>
				<Modal.Header closeButton>
					<Modal.Title>
						{details?.row.product_name}
						<div className="chain-muted fw-normal">
							{t('chain_stock_now')} · {qtyUnit(details?.row.total_balance, details?.row.unit_name)} · {money(details?.row.total_value)}
						</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '520px' }}>
					<table className="chain-table">
						<thead>
							<tr>
								<th>#</th>
								<th>{t('chain_branch')}</th>
								<th>{t('wms')}</th>
								<th className="text-end">{t('balance2')}</th>
								<th className="text-end">{t('chain_avg_cost')}</th>
								<th className="text-end">{t('chain_stock_value')}</th>
							</tr>
						</thead>
						<tbody>
							{detailRows.length === 0 && <ChainEmpty colSpan={6} />}
							{detailRows.map((item, index) => (
								<tr key={`${item.pos_id}-${index}`} style={details?.posId === item.pos_id ? { background: 'rgba(var(--bs-primary-rgb), 0.08)' } : undefined}>
									<td className="chain-index">{index + 1}</td>
									<td>{item.pos_name}</td>
									<td>{item.wms_name}</td>
									<td className="chain-num" style={item.balance < 0 ? NEGATIVE_STYLE : undefined}>{qtyUnit(item.balance, details?.row.unit_name)}</td>
									<td className="chain-num">
										{item.price > 0 ? money(item.price) : <span className="chain-muted" title={t('chain_stock_no_cost_hint')}>—</span>}
									</td>
									<td className="chain-num"><b>{money(item.value)}</b></td>
								</tr>
							))}
						</tbody>
						{detailRows.length > 0 && (
							<tfoot>
								<tr>
									<td></td>
									<td colSpan={2}>{t('total')}</td>
									<td className="chain-num">{qtyUnit(details?.row.total_balance, details?.row.unit_name)}</td>
									<td className="chain-num">{details?.row.avg_price !== null ? money(details?.row.avg_price) : '—'}</td>
									<td className="chain-num">{money(details?.row.total_value)}</td>
								</tr>
							</tfoot>
						)}
					</table>
				</Modal.Body>
				<Modal.Footer>
					<ExcelButton onClick={exportDetails} disabled={detailRows.length === 0} />
					<button type="button" className="btn btn-outline-secondary" onClick={() => setDetails(null)}>{t('close')}</button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

// Стоимость запасов филиалов: что лежит на полках и сколько ушло в минус по учёту
function ValueChart({ branches }) {
	const { t } = useTranslation()
	if (branches.length === 0) return null
	const rows = [...branches].sort((a, b) => b.value - a.value)

	const series = [
		{ name: t('chain_stock_in_stock_value'), data: rows.map(branch => Math.round(branch.value - branch.negative_value)) },
		{ name: t('chain_stock_negative_value'), data: rows.map(branch => Math.round(branch.negative_value)) },
	]
	const options = {
		chart: { type: 'bar', stacked: true, toolbar: { show: false } },
		plotOptions: { bar: { horizontal: true, borderRadius: 2, barHeight: '62%' } },
		colors: ['#5b73e8', '#f06548'],
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: { categories: rows.map(branch => branch.pos_name), labels: { formatter: value => formatMoney(Math.round(value / 1000)) + 'k' } },
		tooltip: { y: { formatter: value => formatMoney(Math.round(value)) } },
	}

	return (
		<div className="card chain-card mb-3">
			<div className="card-body">
				<div className="chain-card-title"><h5>{t('chain_stock_value_by_branches')}</h5></div>
				<ReactApexChart type="bar" height={Math.max(260, rows.length * 40 + 90)} series={series} options={options} />
			</div>
		</div>
	)
}

export default Balance
